import EditableDropdown from '../../../../common/EditableDropdown/EditableDropdown';
import styles from './AuditedFinancialsVisualisation.module.scss';
import React, { useEffect, useRef, useState } from 'react';
import { fetchMisTableOptions } from '../../../../store/company/action';
import { useDispatch } from 'react-redux';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import ClassifiedTable from '../../components/Tables/ClassifiedTable/ClassifiedTable';
import expandMore from '../../../../assets/expand-more.svg';
import editClassificationIcon from './editClassificationIcon.svg';
import { ctaPurpleRight, changeCircleBlue } from '../../../../assets/hostedassets';
import CtaPill from '../../../../common/_custom/CtaPill/CtaPill';
import { EXCEL_ANALYSIS_LABEL, EXCEL_ANALYSIS_LOADING_LABEL } from '../../../../common/_custom/CtaPill/ctaPillConstants';
import RawTable from '../../components/Tables/RawTable/RawTable';
import { isEmpty } from '../../../../utils/utils';
import { DATE_FORMATS, formatDateByFormat } from '../../../../utils/dateUtils';
import { AUDITED_TYPE_CONSOLIDATED, AUDITED_TYPE_STANDALONE } from '../../../../utils/constants/commonConstants';
import FinancialLoaderComponent from '../../../../common/FinancialLoader/FinancialLoaderComponent';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import A4Page from '../../../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../../../store/pdfReport/pdfReport_constants';
import { useMcaPolling } from '../../../../utils/customHooks';
import { useAuditorCommentsAction } from '../../../../common/AuditorCommentsPill/useAuditorCommentsAction';
import { AUDITOR_COMMENTS_LABEL } from '../../../../utils/constants/commonConstants';
import { useElementWidth } from '../../../../utils/customHooks';
import clsx from 'clsx';
import { updateQueryParams } from '../../../../utils/searchParamUtils';
import { useAuditedType, buildConsolidationCategoryOptions } from './useAuditedType';
import ThreeDotMenu from '../../../../common/ThreeDotMenu/ThreeDotMenu';
import CategorySelector from '../../../../common/CategorySelector/CategorySelector';
import FileViewerDrawer from '../../../../common/DocumentViewer/FileViewerDrawer';
import { useAuditedReportDownload } from '../AuditedFinancials/useAuditedReportDownload';
import { useAuditedStatementData } from './useAuditedStatementData';
import { useUnitPreference } from './useUnitPreference';
import { AUDITED_STATEMENT_CONFIGS, AUDITED_STATEMENT_FALLBACK_LABELS } from './auditedStatementTypes';
import AuditedStatementSection from './components/AuditedStatementSection/AuditedStatementSection';
import {
    BALANCE_SHEET_LAYOUT,
    PROFIT_AND_LOSS_LAYOUT,
    CASH_FLOW_LAYOUT,
    CASH_FLOW_INDIRECT_MIS,
    CASH_FLOW_INDIRECT_LAYOUT,
    CASH_FLOW_INDIRECT_TITLE,
    CASH_FLOW_INDIRECT_DISCLOSURE,
    AUDITED_UNIT_DEFAULT,
    AUDITED_UNIT_OPTIONS,
    AUDITED_UNIT_STORAGE_KEY,
    FINANCIAL_YEAR_COLUMN_LABEL,
    keepComputablePeriods,
} from './auditedStatementLayout';
import { useFinancialsIngestionStatus } from '../Financials/financialsIngestion/useFinancialsIngestionStatus';
import { FINANCIALS_FILE_TYPE } from '../Financials/financialsIngestion/constants';
import { useDocIdFileViewer } from '../../../../common/DocumentViewer/useDocIdFileViewer';

// Each row is measured on its own, because they have very different requirements and one threshold
// for both made the actions collapse ~200px before they had to. The meta row is measured twice: the
// MCA fetch message is only mounted WHILE a fetch is in flight, and at rest the row carries the
// much shorter last-updated label instead.
// Measured in Gilroy at `width: max-content` against the content box (what ResizeObserver reports),
// + an 8px buffer, rounded up to the next 5. Re-measure if either row's contents change — nothing
// fails when these drift.
const CONTROLS_ROW_COLLAPSE_WIDTH = 865; // 853 · toggle + Edit Classification | three pills
const META_ROW_FULL_MESSAGE_WIDTH_FETCHING = 1025; // 1013 · title + units | long message + badge
const META_ROW_HIDE_TITLE_WIDTH_RESTING = 810; // 798 · title + units | last-updated + badge
const META_ROW_HIDE_TITLE_WIDTH_FETCHING = 875; // 863 · title + units | short message + badge

const AuditedFinancialsVisualisation = ({ selectedMetric = '', isPDFReport = false, assesseeOrgId = '', companyName = '' }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const queryParams = useParams();

    const [rawOptions, setRawOptions] = useState<any[]>([]);
    const [metricOptions, setMetricOptions] = useState<string[]>([]);
    const [metricOptionsObject, setMetricsOptionsObject] = useState<any[]>([]);

    const orgId = queryParams.id || assesseeOrgId;
    const { mcaDocData } = useMcaPolling(orgId);
    const isMcaCalled = mcaDocData?.isFetching === true;
    // '' when updatedAt is absent OR malformed (formatDateByFormat validates via dayjs), so a
    // bad timestamp renders nothing rather than "Last Updated on ".
    const lastUpdatedLabel = formatDateByFormat(mcaDocData?.updatedAt, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY);

    const fetchOptions = () => {
        dispatch(
            fetchMisTableOptions({
                orgId: orgId || '',
                source: 'AicaAuditedFinancials',
                onSuccess: (res: any) => {
                    const optionsData = res?.data?.data;
                    const misOptionsBlock = optionsData?.[0];
                    setRawOptions(misOptionsBlock?.misOptions);
                    setMetricOptions(misOptionsBlock?.misOptions?.map((item: any) => item.misDisplayName) ?? []);
                    setMetricsOptionsObject(misOptionsBlock?.misOptions || []);

                    const consolidationBlock = optionsData?.find(
                        (block: any) => block?.latestConsolidatedFY !== undefined || block?.latestStandaloneFY !== undefined,
                    );
                    setAuditedTypeFromYears(
                        consolidationBlock?.latestConsolidatedFY ?? null,
                        consolidationBlock?.latestStandaloneFY ?? null,
                    );
                },
            }),
        );
    };

    useEffect(() => {
        orgId && fetchOptions();
    }, [orgId]);

    useEffect(() => {
        if (metricOptions.includes('Other Metric (MIS)')) {
            setMetricOptions(metricOptions.filter((item) => item !== 'Other Metric (MIS)'));
        }
    }, [metricOptions]);

    // ── Redesign — three statements resolved from the tab options, fixed Figma order ──
    const resolvedMetrics = AUDITED_STATEMENT_CONFIGS.reduce(
        (acc, { key, matcher }) => {
            acc[key] = metricOptions.find((option) => matcher.test(option)) ?? '';
            return acc;
        },
        {} as Record<string, string>,
    );

    const balanceSheet = useAuditedStatementData({ orgId, metric: isPDFReport ? '' : resolvedMetrics.balanceSheet });
    const profitAndLoss = useAuditedStatementData({ orgId, metric: isPDFReport ? '' : resolvedMetrics.profitAndLoss });
    const cashFlow = useAuditedStatementData({ orgId, metric: isPDFReport ? '' : resolvedMetrics.cashFlow });
    const cashFlowIndirect = useAuditedStatementData({ orgId, metric: isPDFReport ? '' : CASH_FLOW_INDIRECT_MIS });

    // ── Legacy single-metric report mode (AicaPDFReport, driven by `selectedMetric`) ──
    const [reportMetric, setReportMetric] = useState<string>('');
    useEffect(() => {
        if (!isPDFReport) return;
        if (metricOptionsObject?.length && metricOptions?.length) {
            if (selectedMetric) setReportMetric(selectedMetric);
            else {
                const first = (metricOptionsObject as any[]).find((item) => item?.isDataAvailable === true)?.misDisplayName;
                setReportMetric(first || metricOptions?.[0]);
            }
        }
    }, [isPDFReport, metricOptionsObject, metricOptions, selectedMetric]);

    const reportModeSection = useAuditedStatementData({ orgId, metric: isPDFReport ? reportMetric : '' });

    // ── Consolidation (Standalone / Consolidated) — shared across every statement ──
    const onConsolidationChange = (nature: string) => {
        [balanceSheet, profitAndLoss, cashFlow, cashFlowIndirect, reportModeSection].forEach((section) => {
            section.resetDrillDown();
            section.fetchTableData(nature);
        });
    };

    const { auditedType, auditedTypeOptionsObject, handleConsolidationChange, setAuditedTypeFromYears } =
        useAuditedType(onConsolidationChange);

    // RECUR-28096 — while an uploaded audited file is still being extracted, show a loader in
    // place of "(No data available)" instead of leaving the tab looking empty/broken. No poll in
    // PDF-report mode (A-22). `auditedType` can be `null` before the first consolidation nature is
    // derived, which fetchTableData doesn't accept — fall back to `undefined` (its own "unset").
    const { inProgress: financialsInProgress } = useFinancialsIngestionStatus({
        orgId,
        fileType: FINANCIALS_FILE_TYPE.AUDITED,
        enabled: !isPDFReport,
        onCompleted: () => {
            fetchOptions();
            [balanceSheet, profitAndLoss, cashFlow, cashFlowIndirect].forEach((section) =>
                section.fetchTableData(auditedType ?? undefined),
            );
        },
    });

    // Units selector (RECUR-27010): INR (default — raw) / Lakhs / Crore. Rescaling happens in the
    // table. Validated restore + persist live in useUnitPreference, shared with Provisional
    // Financials (RECUR-27640) so the two surfaces cannot drift on the edge cases.
    const [unit, setUnit] = useUnitPreference(AUDITED_UNIT_STORAGE_KEY);

    // "Raw Data" download/view hook — lifted here so BOTH the full-width inline button and the
    // compact kebab item can trigger it, with the viewer rendered once at page level.
    const { downloading, viewLoading, viewerState, onViewClick, onDownloadClick, closeViewer } = useAuditedReportDownload({
        assesseeOrgId: orgId,
        orgName: companyName,
    });

    // Opens a "Reference Documents" row's source file in the shared FileViewerDrawer — same hook
    // ProvisionalFinancials uses for its own copy of this row (see useProvisionalFinancialsLogic).
    const sourceFileViewer = useDocIdFileViewer();

    // UAT RECUR-26713 (Nishank #20/#21/#22): the inline controls are correct at full width; when the
    // Comments drawer narrows the page they collapse into a 3-dot menu. Driven off the page's own
    // width, so it is decoupled from the drawer internals. Each row stages on its own threshold;
    // the measurements behind them are recorded once, at the const declarations.
    const auditorComments = useAuditorCommentsAction(orgId);
    const [rootRef, rootWidth] = useElementWidth();
    const measured = rootWidth > 0;
    const isCompact = measured && rootWidth < CONTROLS_ROW_COLLAPSE_WIDTH;
    const isMetaRowTight = measured && rootWidth < META_ROW_FULL_MESSAGE_WIDTH_FETCHING;
    const hideMetaTitleWidth = isMcaCalled ? META_ROW_HIDE_TITLE_WIDTH_FETCHING : META_ROW_HIDE_TITLE_WIDTH_RESTING;
    const hideMetaTitle = measured && rootWidth < hideMetaTitleWidth;

    // Truncate the FY column set for the printed report page (unchanged legacy behavior).
    useEffect(() => {
        if (!isPDFReport) return;
        const tableHeadersLength = reportModeSection.tableData?.headers?.length;
        if (!tableHeadersLength) return;

        if (tableHeadersLength > 1) {
            const headers = reportModeSection.tableData.headers;
            const headersForPDFReport = [
                headers[0],
                ...(tableHeadersLength > 3 ? [headers[tableHeadersLength - 3]] : []),
                ...(tableHeadersLength > 2 ? [headers[tableHeadersLength - 2]] : []),
                headers[tableHeadersLength - 1],
            ];

            if (JSON.stringify(headers) !== JSON.stringify(headersForPDFReport)) {
                reportModeSection.setTableData((data: any) => ({ ...data, headers: headersForPDFReport }));
            }
        }
        // reportModeSection.setTableData is a stable state-dispatch setter off the useReportMode
        // hook — safe to omit; depending on the whole hook object would re-run this on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPDFReport, reportModeSection.tableData?.headers]);

    const showAllFinancialMetrics = () => {
        updateQueryParams(searchParams, setSearchParams, { showFinancialMetrics: true }, location);
    };

    // ── Legacy report-mode render — unchanged from the pre-redesign single-metric view ──
    if (isPDFReport) {
        const {
            tableData,
            loading,
            tableType,
            setTableType,
            rawTableData,
            isEditing,
            setIsEditing,
            changedData,
            setChangedData,
            fetchRawData,
            handleSave,
        } = reportModeSection;
        const hasData = !isEmpty(tableData?.headers);

        return tableData?.headers?.length ? (
            <A4Page sectionName={REPORT_SECTION_NAMES.AUDITED_FINANCIALS}>
                <div className={styles.Card}>
                    <div className={styles.Top}>
                        <div className={styles.Title}>
                            Financial Statements&nbsp;
                            <span className={styles.Secondary}>| {reportMetric}</span>
                        </div>
                        <div className={styles.Right}>All values are in INR</div>
                    </div>
                    <div className={styles.Body}>
                        <div className={styles.HeadingContainer}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <h3>Financial Statements</h3>
                                <div className={styles.divider} />
                                <EditableDropdown
                                    id={'MetricSelector'}
                                    optionStyle={{ fontSize: '14px' }}
                                    value={reportMetric}
                                    options={metricOptions}
                                    setOutput={(val) => setReportMetric(val)}
                                    removeInput={true}
                                    placeHolder={'Select Metric'}
                                    optionsObject={metricOptionsObject}
                                />
                            </div>
                        </div>

                        {tableType && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    margin: '0.5rem 0',
                                    padding: '0 0.5rem',
                                }}
                            >
                                <div className={styles.Breadcrumb}>
                                    <span className={styles.Blue} onClick={() => setTableType('')}>
                                        {reportMetric}
                                    </span>
                                    <img src={expandMore} alt="exp" height="10px" />
                                    {tableType}
                                </div>
                                {isEditing ? (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            fontFamily: 'Gilroy-Semibold,serif',
                                            fontSize: '14px',
                                        }}
                                    >
                                        <div>Save Classification</div>
                                        <button
                                            onClick={() => {
                                                setIsEditing(false);
                                                setChangedData([]);
                                            }}
                                            className={styles.CancelClassification}
                                        >
                                            Cancel
                                        </button>
                                        <button onClick={handleSave} className={styles.SaveClassification}>
                                            <img
                                                src={'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/saveClassificationInvestor.svg'}
                                                alt={'save'}
                                            />
                                            Save
                                        </button>
                                    </div>
                                ) : (
                                    <div onClick={() => setIsEditing(true)} className={styles.EditClassification}>
                                        <img
                                            src={'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/editClassification.svg'}
                                            alt="edit"
                                        />
                                        Edit Classification
                                    </div>
                                )}
                            </div>
                        )}
                        <div className={clsx(styles.TableContainer, { [styles.NoTableData]: !hasData })}>
                            {tableType && (
                                <RawTable
                                    data={rawTableData}
                                    loading={loading}
                                    freezeColumnEnd={2}
                                    freezeColumnStart={0}
                                    isEditing={isEditing}
                                    metric={reportMetric}
                                    rawOptions={rawOptions}
                                    changedData={changedData}
                                    setChangedData={setChangedData}
                                />
                            )}
                            {tableType === '' && hasData && (
                                <ClassifiedTable
                                    data={tableData}
                                    loading={false}
                                    handleClick={fetchRawData}
                                    metric={reportMetric}
                                    isPDFReport={isPDFReport}
                                    initialHorizontalScroll="right"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </A4Page>
        ) : (
            <></>
        );
    }

    // ── Redesign — single scrolling page, three collapsible sections (RECUR-27010) ──

    return (
        <div className={styles.AuditedRoot} ref={rootRef}>
            <div className={styles.MetaRow}>
                <div className={styles.MetaLeft}>
                    {!hideMetaTitle && <h2 className={styles.PageTitle}>Audited Financials</h2>}
                    <div className={clsx(styles.UnitsSelect, { [styles.UnitsSelectFlush]: hideMetaTitle })}>
                        <span className={styles.UnitsLabel}>Units ₹:</span>
                        <EditableDropdown
                            id="UnitsSelector"
                            value={unit}
                            options={AUDITED_UNIT_OPTIONS}
                            setOutput={(val: string) => setUnit(val)}
                            removeInput={true}
                            optionStyle={{ fontSize: '14px' }}
                            placeHolder={AUDITED_UNIT_DEFAULT}
                        />
                    </div>
                </div>
                {/* UAT (Aug 2026): this meta row shows exactly one status at a time — the MCA
                    fetching message while a fetch is in flight, else the last-updated date as
                    visible text. When the Comments drawer narrows the page (isCompact) the
                    fetching message drops its "(takes up to 24 hours)" tail and stays on one
                    line; last-updated is already narrow enough. aria-live: the
                    fetching↔last-updated swap is a state change screen readers should announce. */}
                <div className={styles.MetaInfo} aria-live="polite">
                    {isMcaCalled ? (
                        <div className={styles.FetchingMessage}>
                            <FinancialLoaderComponent
                                tableName=""
                                message={
                                    isMetaRowTight
                                        ? 'Fetching latest data from MCA…'
                                        : 'Fetching latest data from MCA (takes up to 24 hours)'
                                }
                                status
                            />
                        </div>
                    ) : (
                        lastUpdatedLabel && <span className={styles.LastUpdated}>Last Updated on {lastUpdatedLabel}</span>
                    )}
                    <div className={styles.SourceBadge}>
                        <span className={styles.SourceBadgeLabel}>Source:</span>
                        <span className={styles.SourceBadgeValue}>MCA</span>
                    </div>
                </div>
            </div>

            <div className={styles.ControlsRow}>
                {/* RECUR-27010 UAT: reuse the shared CategorySelector segmented toggle (as used by
                    Deal View / Mandate View) instead of a bespoke toggle — Standalone / Consolidated
                    text, a nature with no audited data is passed `disabled` so it renders greyed. */}
                <div className={styles.ControlsLeft}>
                    <CategorySelector
                        categoryOptions={buildConsolidationCategoryOptions(auditedTypeOptionsObject)}
                        selectedCategory={auditedType}
                        changeSelectedTab={(type) => handleConsolidationChange(type)}
                    />
                    {/* Sits beside the toggle, not with the pills: it edits this table in place,
                        where every pill navigates away. Still the disabled placeholder — no
                        page-level Edit-Classification backend yet (RECUR-27010). */}
                    {!isCompact && (
                        <button type="button" className={styles.InlineTextAction} title="Coming soon" disabled>
                            <img src={editClassificationIcon} alt="" width={14} height={14} />
                            Edit Classification
                        </button>
                    )}
                </div>
                <div className={styles.ControlsRight}>
                    {/* UAT RECUR-26713 (Nishank #20/#21): full width shows the inline actions; when
                        the Comments drawer narrows the page (isCompact) the secondary actions
                        collapse into a 3-dot menu beside the Auditor's Comments pill. */}
                    {isCompact ? (
                        <ThreeDotMenu
                            color="#6021b3"
                            menuItems={[
                                { name: 'Financial Ratios', onClick: () => showAllFinancialMetrics() },
                                // Auditor's Comments collapses in too, so all three financials tabs
                                // show the same single control when compact.
                                ...(auditorComments.isAvailable ? [{ name: AUDITOR_COMMENTS_LABEL, onClick: auditorComments.open }] : []),
                                // Plain label, no in-flight swap: ThreeDotMenu closes on click (no
                                // keepOpenOnClick), so the row unmounts the moment onViewClick fires and a
                                // "Generating…" state would never be seen. Re-entrancy is already handled
                                // downstream by useAuditedReportDownload's inFlightRef. The full-width
                                // button beside this one carries the visible loading state.
                                { name: EXCEL_ANALYSIS_LABEL, onClick: () => onViewClick() },
                                // No page-level Edit-Classification backend yet — disabled placeholder
                                // (mirrors the disabled inline button at full width).
                                { name: 'Edit Classification', onClick: () => {}, disabled: true },
                            ]}
                        />
                    ) : (
                        <>
                            <CtaPill label="Financial Ratios" icon={ctaPurpleRight} onClick={() => showAllFinancialMetrics()} />
                            {auditorComments.isAvailable && (
                                <CtaPill label={AUDITOR_COMMENTS_LABEL} icon={ctaPurpleRight} onClick={auditorComments.open} />
                            )}
                            <CtaPill
                                tone="blue"
                                icon={changeCircleBlue}
                                label={EXCEL_ANALYSIS_LABEL}
                                loadingLabel={EXCEL_ANALYSIS_LOADING_LABEL}
                                loading={viewLoading}
                                onClick={() => onViewClick()}
                            />
                        </>
                    )}
                    {/* Outside the branch: one hook instance owns the drawer, so it stays open when
                        the row compacts around it. */}
                    {auditorComments.drawer}
                </div>
            </div>

            {/* Row order/labels for all three statements are Figma-driven (see
                auditedStatementLayout.ts), joined to whatever classifications the API
                currently returns by `classificationName` — never the other way around.
                A SINGLE page-level spinner (the shared LoadingSpinner used by Provisional
                Financials etc.) covers the initial/nature-switch fetch — not one loader per
                statement — so the three sections render together once all are ready. */}
            {balanceSheet.loading || profitAndLoss.loading || cashFlow.loading || cashFlowIndirect.loading ? (
                <LoadingSpinner color="black" height="40px" style={{ marginTop: '40px' }} />
            ) : (
                <>
                    <AuditedStatementSection
                        title={resolvedMetrics.balanceSheet || AUDITED_STATEMENT_FALLBACK_LABELS.balanceSheet}
                        layout={BALANCE_SHEET_LAYOUT}
                        tableData={balanceSheet.tableData}
                        loading={false}
                        unit={unit}
                        inProgress={financialsInProgress}
                        firstColumnLabel={FINANCIAL_YEAR_COLUMN_LABEL}
                        referenceDocumentsRow={{ statement: 'balanceSheet', onOpen: sourceFileViewer.open }}
                    />
                    <AuditedStatementSection
                        title={resolvedMetrics.profitAndLoss || AUDITED_STATEMENT_FALLBACK_LABELS.profitAndLoss}
                        layout={PROFIT_AND_LOSS_LAYOUT}
                        tableData={profitAndLoss.tableData}
                        loading={false}
                        unit={unit}
                        inProgress={financialsInProgress}
                        firstColumnLabel={FINANCIAL_YEAR_COLUMN_LABEL}
                        referenceDocumentsRow={{ statement: 'profitAndLoss', onOpen: sourceFileViewer.open }}
                    />
                    <AuditedStatementSection
                        title={resolvedMetrics.cashFlow || AUDITED_STATEMENT_FALLBACK_LABELS.cashFlow}
                        layout={CASH_FLOW_LAYOUT}
                        tableData={cashFlow.tableData}
                        loading={false}
                        unit={unit}
                        inProgress={financialsInProgress}
                        firstColumnLabel={FINANCIAL_YEAR_COLUMN_LABEL}
                        referenceDocumentsRow={{ statement: 'cashFlow', onOpen: sourceFileViewer.open }}
                    />
                    <AuditedStatementSection
                        title={CASH_FLOW_INDIRECT_TITLE}
                        titleInfo={CASH_FLOW_INDIRECT_DISCLOSURE}
                        layout={CASH_FLOW_INDIRECT_LAYOUT}
                        tableData={keepComputablePeriods(cashFlowIndirect.tableData)}
                        loading={false}
                        unit={unit}
                        inProgress={financialsInProgress}
                        firstColumnLabel={FINANCIAL_YEAR_COLUMN_LABEL}
                    />
                </>
            )}

            {/* "Raw Data" viewer — triggered from the actions kebab (Nishank #21). */}
            {!!viewerState && (
                <FileViewerDrawer
                    heading={`Audited Financials — ${companyName || 'Report'}`}
                    fileUrl={viewerState.url}
                    mimeType={viewerState.mimeType}
                    downloading={downloading}
                    downloadLabel="Download"
                    onDownload={onDownloadClick}
                    onClose={closeViewer}
                />
            )}

            {/* Reference Documents row viewer — triggered from a source file chip/popover row. */}
            {sourceFileViewer.viewerProps && <FileViewerDrawer {...sourceFileViewer.viewerProps} />}
        </div>
    );
};

export default AuditedFinancialsVisualisation;
